import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, RefreshCw, HardDrive, Settings, Heart } from 'lucide-react';
import KofiButton from '../components/KofiButton';
import { useBotStatus } from '../hooks/useBotStatus';
import SEO from '../components/SEO';
import { getSoftwareAppSchema, getBreadcrumbSchema } from '../data/schemas';

// Module-scope constant - stable reference, no re-declaration on each render
const sampleLyrics = [
  "Dancing in the moonlight, shadows fade away",
  "Starlight in the atmosphere, guides us on our way",
  "Feel the cadence of the beat, falling into place",
  "Real-time lyrics streaming fast, keeping up the pace",
  "Adjust the latency on the fly, never miss a verse",
  "Synchronized in harmony across the universe"
];

const LyrikaStanzaPage = () => {
  const [offset, setOffset] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState(2);
  const [copiedCommand, setCopiedCommand] = useState(null);

  const isLyrikaSubdomain = typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('lyrika.');

  // Live status query hook (polling 2x an hour / 30 mins)
  const botHealthUrl = process.env.REACT_APP_BOT_HEALTH_URL || "https://lyrika.wispbyte.app/health";
  const { isOnline } = useBotStatus(botHealthUrl);

  // Auto-advance lyrics simulation every 3.2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLineIndex((prev) => (prev + 1) % sampleLyrics.length);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const adjustOffset = (amount) => {
    setOffset((prev) => parseFloat((prev + amount).toFixed(1)));
  };

  const copyCommand = (cmdText) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(cmdText);
      setCopiedCommand(cmdText);
      setTimeout(() => setCopiedCommand(null), 2000);
    }
  };

  return (
    <div className="text-gray-100 font-sans relative selection:bg-pink-500/30">
      <SEO
        title="Lyrika-Stanza — Synchronized Real-Time Lyrics Discord Bot"
        description="Stream real-time synchronized scrolling lyrics directly into your Discord channels with millisecond latency offset calibration. Hosted 24/7 on Wispbyte by NotKoneko."
        path={isLyrikaSubdomain ? '/' : '/projects/discordbots/lyrika-stanza'}
        customDomain={isLyrikaSubdomain ? 'https://lyrika.notkoneko.space' : 'https://portfolio.notkoneko.space'}
        keywords={['Lyrika', 'Lyrika-Stanza', 'Discord Lyrics Bot', 'Discord Music Lyrics', 'Synchronized Lyrics Discord', 'Koneko', 'NotKoneko', 'Wispbyte Bot', 'LRCLIB Discord Bot']}
        image="/lyrika-avatar.webp"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            getSoftwareAppSchema({
              name: 'Lyrika-Stanza',
              description: 'Synchronized, real-time scrolling lyrics engine for voice channel music bots on Discord with interactive timing offset adjustment controls.',
              applicationCategory: 'MultimediaApplication / Discord Bot',
              operatingSystem: 'Discord / Cloud Service',
              url: isLyrikaSubdomain ? 'https://lyrika.notkoneko.space' : '/projects/discordbots/lyrika-stanza',
              codeRepository: 'https://github.com/NottKoneko/Lyrika-Stanza',
              screenshot: '/lyrika-avatar.webp',
              featureList: [
                'Live Scrolling Lyrics with Focus Frame Target',
                'Interactive Latency Sync (-0.5s / +0.5s)',
                'Bot Agnostic Track Parsing (Chip, Jockie, FredBoat)',
                'Zero-config SQLite Multi-server Architecture'
              ]
            }),
            ...(!isLyrikaSubdomain ? [getBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Projects', url: '/projects' },
              { name: 'Discord Bots', url: '/projects/discordbots' },
              { name: 'Lyrika-Stanza', url: '/projects/discordbots/lyrika-stanza' }
            ])] : [])
          ]
        }}
      />
      <div className="max-w-5xl mx-auto relative z-10 space-y-16">
        
        {/* Dedicated Bot Site Header */}
        <header className="flex flex-col md:flex-row items-center justify-between gap-4 py-4 px-4 sm:px-6 bg-[#090714]/80 backdrop-blur-xl border border-purple-500/20 rounded-2xl sm:rounded-3xl shadow-2xl">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
            {isLyrikaSubdomain ? (
              <a 
                href="https://portfolio.notkoneko.space/projects/discordbots" 
                className="text-xs font-mono text-gray-400 hover:text-fuchsia-300 transition-colors mb-1 sm:mb-0"
              >
                ← Portfolio
              </a>
            ) : (
              <Link 
                to="/projects/discordbots" 
                className="text-xs font-mono text-gray-400 hover:text-fuchsia-300 transition-colors mb-1 sm:mb-0"
              >
                ← Portfolio
              </Link>
            )}
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-pink-500 via-purple-600 to-indigo-600 p-0.5 shadow-lg shadow-purple-500/20 overflow-hidden">
                <img 
                  src="/lyrika-avatar.webp" 
                  alt="Lyrika-Stanza Profile" 
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              <span 
                className={`absolute -bottom-1 -right-1 w-4 h-4 ${isOnline ? 'bg-emerald-500' : 'bg-red-500'} border-2 border-[#090714] rounded-full flex items-center justify-center transition-colors`} 
                title={isOnline ? "Online on Wispbyte" : "Offline / Maintenance"}
              >
                {isOnline && <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />}
              </span>
            </div>
            <div>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5 sm:gap-2">
                <h1 className="text-xl sm:text-2xl font-serif font-bold tracking-wide text-white">Lyrika-Stanza</h1>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-pink-950/60 text-pink-300 border border-pink-500/40 rounded">
                  BOT
                </span>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded flex items-center gap-1">
                  ✓ VERIFIED
                </span>
              </div>
              <p className="text-xs font-mono text-gray-400 mt-1 sm:mt-0">Activity: <span className="text-fuchsia-400">Listening to Meow Miau Meow</span></p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 sm:gap-3 w-full md:w-auto">
            <KofiButton variant="compact" username="notkoneko" />
            <a 
              href="https://discord.com/oauth2/authorize?client_id=1519789441143537784"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 sm:px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 border border-purple-400/30 text-white text-xs font-mono font-bold rounded-xl transition-all shadow-lg shadow-purple-600/25 flex items-center gap-1.5 transform hover:scale-105 shrink-0"
            >
              <span>+</span> Invite Bot
            </a>
          </div>
        </header>

        {/* Hero Section */}
        <section className="text-center space-y-6 pt-4 sm:pt-6 max-w-3xl mx-auto flex flex-col items-center">
          <div className="relative group flex items-center justify-center">
            {/* Ambient Background Glow Layer */}
            <div className="absolute -inset-4 bg-gradient-to-tr from-pink-600 via-purple-600 to-indigo-500 rounded-full blur-2xl opacity-75 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
            
            {/* Outer Glassmorphic Border Ring */}
            <div className="relative p-3 rounded-full bg-white/10 backdrop-blur-2xl border border-purple-400/30 shadow-[0_0_50px_rgba(232,121,249,0.35)] transform group-hover:scale-105 transition-all duration-300">
              {/* Inner Gradient Border Ring */}
              <div className="p-1 rounded-full bg-gradient-to-tr from-pink-500 via-purple-500 to-indigo-400">
                {/* Avatar Image */}
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-2 border-[#090714] shadow-2xl">
                  <img 
                    src="/lyrika-avatar.webp" 
                    alt="Lyrika Cat Avatar" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <span className={`absolute bottom-1 right-1 px-2.5 py-0.5 ${isOnline ? 'bg-emerald-500 text-[#090714]' : 'bg-red-500 text-white'} font-mono font-black text-[10px] rounded-full shadow-xl border-2 border-[#090714] flex items-center gap-1 z-10 transition-colors`}>
              {isOnline ? (
                <>
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" /> ONLINE
                </>
              ) : (
                'OFFLINE'
              )}
            </span>
          </div>

          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-pink-950/40 border border-pink-500/30 text-pink-400 text-[11px] sm:text-xs font-mono">
            <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" />
            Live Voice Channel Lyric Streaming Engine
          </div>

          <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
            Synchronized Lyrics <br className="hidden sm:inline" />
            <span className="gradient-text-pink-purple-indigo">
              For Your Discord Music Bots.
            </span>
          </h2>

          <p className="text-base sm:text-lg text-gray-300 leading-relaxed font-normal">
            Lyrika-Stanza automatically detects track changes from popular music bots like <strong className="text-white">Chip</strong>, <strong className="text-white">Jockie Music</strong>, and <strong className="text-white">FredBoat</strong>, streaming sliding real-time lyrics directly into your text channels with interactive latency adjustments.
          </p>

          {/* Hero Action Bar: Clean, spacious resource links */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <a 
              href="https://discord.com/oauth2/authorize?client_id=1519789441143537784"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:from-pink-500 hover:to-indigo-500 text-white text-xs font-bold rounded-xl transition-all shadow-xl shadow-pink-500/20 flex items-center gap-2 transform hover:scale-105 active:scale-95"
            >
              <span>+</span> Add Lyrika to Discord
            </a>

            <a
              href="https://github.com/NottKoneko/Lyrika-Stanza/tree/wispbyte"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 bg-[#0e0e18]/90 hover:bg-[#161626] border border-gray-700/80 hover:border-pink-500/40 text-gray-200 hover:text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 shadow-lg transform hover:scale-105 active:scale-95 group/gh"
            >
              <svg className="w-4 h-4 fill-current text-gray-400 group-hover/gh:text-white transition-colors" viewBox="0 0 24 24">
                <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
              </svg>
              <span>GitHub Repo</span>
            </a>

            <button
              disabled
              className="px-4 py-3 bg-[#0e0e18]/70 border border-indigo-500/30 text-indigo-300 text-xs font-bold rounded-xl flex items-center gap-2 cursor-not-allowed opacity-85 shadow-lg"
              title="Discord Support Server Coming Soon"
            >
              <svg className="w-4 h-4 fill-current text-indigo-400" viewBox="0 0 127.14 96.36">
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1,105.25,105.25,0,0,0,32.19-16.14c2.64-27.38-4.51-51.11-18.91-72.15ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,45.91,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,45.91,96.12,53,91.08,65.69,84.69,65.69Z"/>
              </svg>
              <span>Support Server</span>
              <span className="text-[9px] font-mono font-extrabold px-1.5 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 rounded uppercase">
                Soon
              </span>
            </button>
          </div>
        </section>

        {/* Live Interactive Simulator Showcase */}
        <section className="bg-[#0b0b12] border border-pink-500/20 rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-2xl relative overflow-hidden">
          <div className="flex items-center justify-between mb-6 border-b border-gray-800/80 pb-4">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-red-500/80" />
              <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <span className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="text-xs font-mono text-gray-400 ml-2">#lyrics-live-feed</span>
            </div>
            <span className="text-xs font-mono text-pink-400 bg-pink-500/10 px-3 py-1 rounded-full border border-pink-500/20 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" /> LIVE VISUALIZER DEMO
            </span>
          </div>

          {/* Mock Discord Lyric Card */}
          <div className="bg-[#090714] border border-purple-500/20 rounded-2xl p-6 shadow-inner space-y-6">
            <div className="flex items-center justify-between border-b border-purple-500/20 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-xl text-fuchsia-400">
                  🎧
                </div>
                <div>
                  <h4 className="text-base font-serif font-bold text-white">Starlight Serenade (Live Sync)</h4>
                  <p className="text-xs font-mono text-gray-400">Tracked Target: <span className="text-pink-400">@Chip Music Bot</span></p>
                </div>
              </div>
              <span className="text-xs font-mono text-gray-400 bg-[#0d0a1c] px-3 py-1.5 rounded-lg border border-purple-500/20">
                Sync Offset: <span className="text-fuchsia-300 font-bold">{offset > 0 ? `+${offset}` : offset}s</span>
              </span>
            </div>

            {/* Scrolling Lyrics Box with Focus Pointer 👉 */}
            <div className="font-mono text-sm space-y-2 py-2 bg-[#0d0a1c] p-4 rounded-xl border border-purple-500/20">
              {sampleLyrics.map((line, idx) => {
                const isCurrent = idx === currentLineIndex;
                return (
                  <div 
                    key={idx} 
                    className={`flex items-center gap-3 py-1.5 px-3 rounded-lg transition-all duration-300 ${
                      isCurrent 
                        ? 'bg-fuchsia-500/15 text-fuchsia-300 font-bold border-l-4 border-fuchsia-500 scale-[1.01] shadow-md' 
                        : 'text-gray-500 opacity-60'
                    }`}
                  >
                    <span className="w-6 text-center text-fuchsia-400 font-bold">
                      {isCurrent ? '👉' : ''}
                    </span>
                    <span className="flex-1">{line}</span>
                  </div>
                );
              })}
            </div>

            {/* Interactive Discord Action Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => adjustOffset(-0.5)}
                  className="px-4 py-2 bg-[#0d0a1c] hover:bg-purple-950/60 border border-purple-500/30 text-fuchsia-300 text-xs font-mono font-bold rounded-lg transition-colors flex items-center gap-1.5 active:scale-95"
                >
                  <span>⏪</span> -0.5s Latency
                </button>
                <button 
                  onClick={() => adjustOffset(0.5)}
                  className="px-4 py-2 bg-[#0d0a1c] hover:bg-purple-950/60 border border-purple-500/30 text-fuchsia-300 text-xs font-mono font-bold rounded-lg transition-colors flex items-center gap-1.5 active:scale-95"
                >
                  <span>⏩</span> +0.5s Speedup
                </button>
              </div>

              <span className="text-xs font-mono text-gray-400">
                Click timing buttons to test micro-offset adjustments on the fly!
              </span>
            </div>
          </div>
        </section>

        {/* Core Capabilities */}
        <section className="space-y-6">
          <div className="text-center max-w-xl mx-auto">
            <h3 className="text-3xl font-serif font-bold text-white">Built for Low-Latency Performance</h3>
            <p className="text-sm text-gray-400 mt-2 font-mono">Built from the ground up for reliable Discord execution.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#090714] p-6 border border-purple-500/20 rounded-2xl space-y-3 hover:border-pink-500/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-pink-500/20 text-pink-400 flex items-center justify-center">
                <RefreshCw className="w-5 h-5" />
              </div>
              <h4 className="text-lg font-serif font-bold text-white">Bot Agnostic Track Parsing</h4>
              <p className="text-xs text-gray-400 leading-relaxed font-mono">
                Automatically strips emojis, markdown formatting, and target tags from music bot "Now Playing" embeds to resolve exact track titles.
              </p>
            </div>

            <div className="bg-[#090714] p-6 border border-purple-500/20 rounded-2xl space-y-3 hover:border-fuchsia-500/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-fuchsia-400 flex items-center justify-center">
                <HardDrive className="w-5 h-5" />
              </div>
              <h4 className="text-lg font-serif font-bold text-white">Multi-Server SQLite Architecture</h4>
              <p className="text-xs text-gray-400 leading-relaxed font-mono">
                Persistent multi-server configuration storage optimized specifically for high-availability 24/7 deployment on Wispbyte hosts.
              </p>
            </div>

            <div className="bg-[#090714] p-6 border border-purple-500/20 rounded-2xl space-y-3 hover:border-purple-400/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 flex items-center justify-center">
                <Settings className="w-5 h-5" />
              </div>
              <h4 className="text-lg font-serif font-bold text-white">Zero Local Config Files</h4>
              <p className="text-xs text-gray-400 leading-relaxed font-mono">
                Fully managed within Discord via Slash Commands with granular permission controls for server administrators.
              </p>
            </div>
          </div>
        </section>

        {/* Slash Commands Reference Directory */}
        <section className="bg-[#090714] border border-purple-500/20 rounded-3xl p-8 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-purple-500/20 pb-4">
            <div>
              <h3 className="text-2xl font-serif font-bold text-white flex items-center gap-2">
                <span className="text-fuchsia-400 font-mono">&gt;_</span> Slash Command Directory
              </h3>
              <p className="text-xs text-gray-400 font-mono mt-1">Default permissions require Manage Channels or Administrator</p>
            </div>
            <span className="text-xs font-mono text-purple-300 bg-[#0d0a1c] px-3 py-1 rounded-lg border border-purple-500/20">
              Discord API v14
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-xs">
            <div className="bg-[#0d0a1c] p-4 rounded-xl border border-purple-500/20 space-y-2 hover:border-fuchsia-500/40 transition-colors">
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => copyCommand('/setup-lyrics')}
                  className="flex items-center gap-2 text-fuchsia-400 font-bold text-sm hover:text-fuchsia-300 transition-colors group/cmd"
                  title="Click to copy command"
                >
                  <span>/setup-lyrics</span>
                  <span className="text-[10px] font-normal px-2 py-0.5 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded group-hover/cmd:bg-purple-900/60 transition-all">
                    {copiedCommand === '/setup-lyrics' ? '✓ Copied!' : '📋 Copy'}
                  </span>
                </button>
                <span className="text-[10px] text-purple-300 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">Admin Only</span>
              </div>
              <p className="text-gray-300 font-sans text-xs">Assigns the listening input channel and live visualizer output channel for the server.</p>
              <div className="text-[11px] text-gray-400 bg-[#090714] p-2 rounded border border-purple-500/20">
                Options: <span className="text-gray-200">listen-channel</span>, <span className="text-gray-200">output-channel</span>
              </div>
            </div>

            <div className="bg-[#0d0a1c] p-4 rounded-xl border border-purple-500/20 space-y-2 hover:border-pink-500/40 transition-colors">
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => copyCommand('/set-targetbot')}
                  className="flex items-center gap-2 text-pink-400 font-bold text-sm hover:text-pink-300 transition-colors group/cmd"
                  title="Click to copy command"
                >
                  <span>/set-targetbot</span>
                  <span className="text-[10px] font-normal px-2 py-0.5 bg-pink-950/60 text-pink-300 border border-pink-500/40 rounded group-hover/cmd:bg-pink-900/60 transition-all">
                    {copiedCommand === '/set-targetbot' ? '✓ Copied!' : '📋 Copy'}
                  </span>
                </button>
                <span className="text-[10px] text-purple-300 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">Admin Only</span>
              </div>
              <p className="text-gray-300 font-sans text-xs">Designates the specific server music bot (e.g. @Chip) that Lyrika-Stanza will listen to.</p>
              <div className="text-[11px] text-gray-400 bg-[#090714] p-2 rounded border border-purple-500/20">
                Options: <span className="text-gray-200">bot (User tag)</span>
              </div>
            </div>

            <div className="bg-[#0d0a1c] p-4 rounded-xl border border-purple-500/20 space-y-2 hover:border-purple-400/40 transition-colors">
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => copyCommand('/setoffset')}
                  className="flex items-center gap-2 text-fuchsia-400 font-bold text-sm hover:text-fuchsia-300 transition-colors group/cmd"
                  title="Click to copy command"
                >
                  <span>/setoffset</span>
                  <span className="text-[10px] font-normal px-2 py-0.5 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded group-hover/cmd:bg-purple-900/60 transition-all">
                    {copiedCommand === '/setoffset' ? '✓ Copied!' : '📋 Copy'}
                  </span>
                </button>
                <span className="text-[10px] text-purple-300 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">Admin Only</span>
              </div>
              <p className="text-gray-300 font-sans text-xs">Sets server-wide synchronization timing offset in milliseconds (positive = speed up, negative = slow down).</p>
              <div className="text-[11px] text-gray-400 bg-[#090714] p-2 rounded border border-purple-500/20">
                Options: <span className="text-gray-200">offset-ms (Integer)</span>
              </div>
            </div>

            <div className="bg-[#0d0a1c] p-4 rounded-xl border border-purple-500/20 space-y-2 hover:border-fuchsia-500/40 transition-colors">
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => copyCommand('/status')}
                  className="flex items-center gap-2 text-fuchsia-400 font-bold text-sm hover:text-fuchsia-300 transition-colors group/cmd"
                  title="Click to copy command"
                >
                  <span>/status</span>
                  <span className="text-[10px] font-normal px-2 py-0.5 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded group-hover/cmd:bg-purple-900/60 transition-all">
                    {copiedCommand === '/status' ? '✓ Copied!' : '📋 Copy'}
                  </span>
                </button>
                <span className="text-[10px] text-purple-300 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">Admin Only</span>
              </div>
              <p className="text-gray-300 font-sans text-xs">Displays the current server channel configuration, active target bot, and latency offset.</p>
              <div className="text-[11px] text-gray-400 bg-[#090714] p-2 rounded border border-purple-500/20">
                Options: <span className="text-gray-400">None (Returns embed status)</span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#090714] border border-purple-500/30 rounded-3xl p-8 text-center space-y-4 shadow-xl">
          <h3 className="text-2xl font-serif font-bold text-white flex items-center justify-center gap-2">
            <Heart className="w-6 h-6 text-pink-400 fill-pink-500/20" /> Support Lyrika-Stanza
          </h3>
          <p className="text-sm text-gray-300 max-w-2xl mx-auto leading-relaxed font-serif italic">
            — Keeping live scrolling lyrics synchronized 24/7 requires API bandwidth, high-frequency WebSockets, and continuous hosting resources on Wispbyte. If Lyrika-Stanza enhances your server's listening parties, consider supporting the developer!
          </p>
          <div className="pt-2">
            <KofiButton variant="full" username="notkoneko" />
          </div>
        </section>

        {/* Footer with Discreet ToS and Privacy Buttons */}
        <footer className="pt-12 pb-6 border-t border-purple-500/20 flex flex-wrap items-center justify-between gap-4 text-xs font-mono text-gray-400">
          <div>
            <p>© {new Date().getFullYear()} Lyrika-Stanza Bot • Created by <span className="text-fuchsia-300">NotKoneko</span></p>
            <p className="text-[11px] text-gray-500 mt-0.5">Deploys on Wispbyte Node Engine • Discord.js v14.15</p>
          </div>

          {/* Discreet Links */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <a
              href="https://github.com/NottKoneko/Lyrika-Stanza/tree/wispbyte"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-fuchsia-300 transition-colors underline decoration-purple-900 hover:decoration-fuchsia-400 text-xs flex items-center gap-1"
            >
              GitHub Repo
            </a>
            <span>•</span>
            <span className="text-gray-500 text-xs flex items-center gap-1 cursor-not-allowed" title="Discord Support Server Coming Soon">
              Support Server <span className="text-[9px] font-mono px-1 py-0.2 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded">SOON</span>
            </span>
            <span>•</span>
            <Link 
              to={isLyrikaSubdomain ? "/tos" : "/projects/discordbots/lyrika-stanza/tos"} 
              className="text-gray-400 hover:text-fuchsia-300 transition-colors underline decoration-purple-900 hover:decoration-fuchsia-400 text-xs"
            >
              Terms of Service
            </Link>
            <span>•</span>
            <Link 
              to={isLyrikaSubdomain ? "/privacy" : "/projects/discordbots/lyrika-stanza/privacy"} 
              className="text-gray-400 hover:text-fuchsia-300 transition-colors underline decoration-purple-900 hover:decoration-fuchsia-400 text-xs"
            >
              Privacy Policy
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LyrikaStanzaPage;
